import type { ReactNode } from "react";
import { cx } from "@strust/ui/css";
import { sidebar } from "@strust/ui/recipes";
import { slots } from "../slots.ts";
import { IconButton } from "../primitives/button.tsx";
import { ChevronsLeftIcon, ChevronsRightIcon, ShieldCheckIcon } from "../icons/icons.tsx";

export interface SidebarItem {
  readonly id: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly current?: boolean;
}

export interface SidebarGroup {
  readonly id: string;
  /** Overline of the group (INVESTIGATE, ADMINISTER, PLATFORM), from i18n. */
  readonly label: string;
  readonly items: readonly SidebarItem[];
}

export interface SidebarLinkProps {
  readonly item: SidebarItem;
  readonly className: string;
  readonly children: ReactNode;
}

export interface SidebarProps {
  /** Accessible name of the navigation landmark (from i18n). */
  readonly label: string;
  readonly groups: readonly SidebarGroup[];
  /** Renders one link; the app passes its router link so the menu stays router-agnostic. */
  readonly renderLink: (props: SidebarLinkProps) => ReactNode;
  /** Renders the brand link (logo to Home). */
  readonly renderBrand: (props: {
    readonly className: string;
    readonly children: ReactNode;
  }) => ReactNode;
  readonly brandName: string;
  readonly productName: string;
  readonly collapsed: boolean;
  readonly onToggle: () => void;
  readonly collapseLabel: string;
  readonly expandLabel: string;
  /** Version and environment, shown at the bottom when expanded. */
  readonly footer?: ReactNode;
}

/**
 * Collapsible side menu of the Explorer: brand mark, grouped links with the accent rail on the
 * current page; collapsed, it shows icons only and keeps each link's accessible name.
 */
export function Sidebar({
  label,
  groups,
  renderLink,
  renderBrand,
  brandName,
  productName,
  collapsed,
  onToggle,
  collapseLabel,
  expandLabel,
  footer,
}: SidebarProps) {
  const s = slots(sidebar({ collapsed }));
  return (
    <aside className={s.root} data-collapsed={collapsed || undefined}>
      <div className={s.head}>
        {renderBrand({
          className: s.brand,
          children: (
            <>
              <span aria-hidden="true" className={s.mark}>
                <ShieldCheckIcon />
              </span>
              <span className={s.wordmark}>
                <span className={s.name}>{brandName}</span>
                <span className={s.product}>{productName}</span>
              </span>
            </>
          ),
        })}
        <IconButton
          label={collapsed ? expandLabel : collapseLabel}
          icon={collapsed ? <ChevronsRightIcon /> : <ChevronsLeftIcon />}
          visual="ghost"
          size="sm"
          aria-expanded={!collapsed}
          onClick={onToggle}
        />
      </div>
      <nav aria-label={label} className={s.nav}>
        {groups
          .filter((group) => group.items.length > 0)
          .map((group) => (
            <div key={group.id} className={s.group}>
              <p className={s.groupLabel} aria-hidden={collapsed || undefined}>
                {group.label}
              </p>
              <ul className={s.list} aria-label={group.label}>
                {group.items.map((item) => (
                  <li key={item.id}>
                    {renderLink({
                      item,
                      className: s.link,
                      children: (
                        <>
                          {item.current ? <span aria-hidden="true" className={s.rail} /> : null}
                          <span aria-hidden="true" className={s.icon}>
                            {item.icon}
                          </span>
                          <span className={cx(s.label)}>{item.label}</span>
                        </>
                      ),
                    })}
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </nav>
      {footer && !collapsed ? <div className={s.footer}>{footer}</div> : null}
    </aside>
  );
}
